/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-magic-numbers */
/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  ItemStyleOption,
  SeriesLabelOption,
  SymbolOptionMixin,
  StatesOptionMixin,
  DefaultEmphasisFocus,
  OptionDataItemObject,
  OptionDataValue,
  CallbackDataParams,
  SeriesOption,
  SeriesOnCartesianOptionMixin,
  SeriesOnSingleOptionMixin
} from 'echarts/lib/util/types';
import Single from 'echarts/lib/coord/single/Single';
import SeriesModel from 'echarts/lib/model/Series';
import GlobalModel from 'echarts/lib/model/Global';
import SeriesData from 'echarts/lib/data/SeriesData';
import createSeriesData from 'echarts/lib/chart/helper/createSeriesData';
import { SeriesMaskConfig, ImageMaskConfig } from './type';

/** 类型别名 */
export type Word = string;
export type Weight = number;

/** 无轴时数据类型 */
export type WordCloudNormalDataValue = [Word, Weight];

/** 单轴时数据类型 */
export type WordCloudSingleAxisDataValue = [OptionDataValue, Word, Weight];

/**
 * 定义数据类型
 */
export type WordCloudDataValue = WordCloudNormalDataValue | WordCloudSingleAxisDataValue;

/** 自定义比例尺的类型 */
export type WordCloudCustomScale = (value: any) => number;

/** 样式扩展 */
export interface WordCloudItemStyleOption<TCbParams = never> extends ItemStyleOption<TCbParams> {
  /** 图形之间的间距，仅适用于外接矩形（或类似图形：圆、圆角矩形等），不适用于自定义图形（不沿图形路径扩展） */
  margin?: number | number[];

  /** 文字与图形的间距 */
  padding?: number | number[];
}

/** 状态类型申明 */
export interface WordCloudStateOption<TCbParams = never> {
  itemStyle?: WordCloudItemStyleOption<TCbParams>;
  label?: SeriesLabelOption;
}

/** 状态类型接口 */
export interface WordCloudStatesMixin {
  emphasis?: {
    focus?: DefaultEmphasisFocus;
    scale?: boolean | number;
  };
}

/** 数据对象类型 */
export interface WordCloudDataItemOption
  extends SymbolOptionMixin,
    WordCloudStateOption,
    StatesOptionMixin<WordCloudStateOption, WordCloudStatesMixin>,
    OptionDataItemObject<WordCloudDataValue> {}

export interface WordCloudBaseLayoutOption {
  /** 是否开启调试模式 */
  isDebug?: boolean;
  /** 布局精度，值越大越不精确 */
  precision?: number;

  /** 重叠面积阈值
   * 给定一个阈值，如果一个文字与周围文字重叠的面积的平均值低于该阈值，仍给予显示
   * 即非严格布局，避免因为个位数级别的重叠导致文字无法布局
   */
  overlapAreaThreshold?: number;

  /** URL、DataURI、series layout */
  mask?: SeriesMaskConfig | ImageMaskConfig | null;

  /** 填充模式，与mask配合使用
   * contain:仅渲染一次
   * cover:多次渲染填充容器
   * fill:仅渲染一次且通过等比拉伸图形填充容器 */
  // TODO: cover、fill的实现
  fillMode?: 'contain' | 'cover' | 'fill';

  // 设定字与图形的间距
  padding?: number | number[];

  // 设定字/图形之间的间距
  margin?: number | number[];

  // 是否要按照文字来布局，默认按照包裹文字的图形布局
  isLayoutByText?: boolean;
}

export interface WordCloudSingleLayoutOption extends WordCloudBaseLayoutOption {
  // 单轴布局时，模糊布局的范围，最小值为0
  fuzzyWidth?: number;
  // 是否保持边缘对齐
  // 即主轴俩侧超出grid的word会向内收敛
  // isEdgeAlign的优先级大于fuzzyWidth
  // 即可能出现对齐边缘后，word的偏移量大于fuzzyWidth
  // 如果提供了数字，则只有权重大于该值的才会对齐
  edgeAlign?: boolean | number;

  // 是否开启单词聚合
  // 传入数字则可定义重叠比：0~1
  // 0即不聚合，1即单轴维度完全覆盖，默认0.3
  aggregation?: boolean | number;
}

export interface WordCloudNormalLayoutOption extends WordCloudBaseLayoutOption {
  // 旋转角度
  // TODO: 旋转角度的实现
  // [0, 2*Math.PI]
  rotateRange: [number, number];
  // [1, 10]
  rotateSplitCount: number;
}

export interface WordCloudSeriesOption
  extends SeriesOption<WordCloudStateOption<CallbackDataParams>, WordCloudStatesMixin>,
    WordCloudStateOption<CallbackDataParams>,
    SymbolOptionMixin<CallbackDataParams>,
    SeriesOnCartesianOptionMixin,
    SeriesOnSingleOptionMixin {
  type?: 'wordCloud';

  /** 目前支持的坐标系 */
  coordinateSystem?: 'none' | 'singleAxis';

  cursor?: string;

  data?: (WordCloudDataItemOption | WordCloudDataValue)[];

  fontSizeRange?: [number, number];

  fontSizeScale?: 'linear' | 'sqrt' | 'log' | WordCloudCustomScale;

  fontWeightRange?: [number, number];

  fontWeightScale?: 'linear' | 'sqrt' | 'log' | WordCloudCustomScale;

  layout?: WordCloudSingleLayoutOption | WordCloudNormalLayoutOption;
}

class WordCloudSeries extends SeriesModel<WordCloudSeriesOption> {
  static readonly type = 'series.wordCloud';

  type = WordCloudSeries.type;

  static readonly dependencies = ['singleAxis'];

  coordinateSystem: Single | null = null;

  hasSymbolVisual = true;

  // eslint-disable-next-line
  getInitialData(option: WordCloudSeriesOption, ecModel: GlobalModel): SeriesData {
    return createSeriesData(null, this, {
      useEncodeDefaulter: true
    });
  }

  static defaultOption: WordCloudSeriesOption = {
    type: 'wordCloud',

    coordinateSystem: 'none',

    symbol: 'rect',

    // eslint-disable-next-line no-magic-numbers
    fontSizeRange: [12, 24],

    fontSizeScale: 'linear',

    // eslint-disable-next-line no-magic-numbers
    fontWeightRange: [300, 600],

    fontWeightScale: 'linear',

    label: {
      show: true
    },

    layout: {
      isDebug: false,
      precision: 2,
      overlapAreaThreshold: 10,
      mask: null,
      fillMode: 'contain',
      padding: 1,
      margin: 0,
      fuzzyWidth: 10,
      edgeAlign: false,
      aggregation: 0.3,
      isLayoutByText: false
    },

    z: 999
  };
}

export default WordCloudSeries;
